import {QR_LOGO_MAX_RATIO, QR_LOGO_PAD_RATIO, QR_LOGO_RADIUS_RATIO} from "./surveyQrBranding.util"

const DOWNLOAD_SIZE = 1024

const saveCanvas = (canvas: HTMLCanvasElement, fileName: string) => {
    canvas.toBlob((blob) => {
        if (!blob) return

        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${fileName}-qr-code.png`
        link.click()
        URL.revokeObjectURL(url)
    })
}

const traceRoundedRect = (ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) => {
    const r = Math.min(radius, width / 2, height / 2)

    ctx.beginPath()
    ctx.moveTo(x + r, y)
    ctx.arcTo(x + width, y, x + width, y + height, r)
    ctx.arcTo(x + width, y + height, x, y + height, r)
    ctx.arcTo(x, y + height, x, y, r)
    ctx.arcTo(x, y, x + width, y, r)
    ctx.closePath()
}

/**
 * Repaints the centre logo onto the exported canvas, mirroring the DOM overlay in `SurveyQrCode`:
 * same 20% cap, same padded white pad, same centre. Returns false when the image carries no usable
 * dimensions - an SVG with no intrinsic size reports 0 in some browsers, and scaling from that
 * would put a NaN rectangle on the canvas - so the caller can still ship the plain code.
 */
const drawCentreLogo = (ctx: CanvasRenderingContext2D, logo: HTMLImageElement) => {
    if (!logo.naturalWidth || !logo.naturalHeight) return false

    const box = DOWNLOAD_SIZE * QR_LOGO_MAX_RATIO
    const scale = Math.min(box / logo.naturalWidth, box / logo.naturalHeight)
    const width = logo.naturalWidth * scale
    const height = logo.naturalHeight * scale
    const pad = box * QR_LOGO_PAD_RATIO
    const centre = DOWNLOAD_SIZE / 2

    ctx.fillStyle = '#ffffff'
    traceRoundedRect(ctx, centre - width / 2 - pad, centre - height / 2 - pad, width + pad * 2, height + pad * 2, box * QR_LOGO_RADIUS_RATIO)
    ctx.fill()
    ctx.drawImage(logo, centre - width / 2, centre - height / 2, width, height)

    return true
}

/**
 * Renders the QR code inside `wrapper` to a PNG and hands it to the browser as a download.
 * Extracted from the former inline QR panel so the fullscreen handover is the single place that
 * offers the download - the rendering itself is unchanged: white background, {@link DOWNLOAD_SIZE}px.
 *
 * The SVG is cloned and pinned to a pixel box before serialising. The fullscreen code is sized with
 * a CSS length ('min(70vw, 70vh)'), and viewport units no longer resolve once the markup is detached
 * and drawn through an <img>; the viewBox does the real scaling, so fixing width/height gives every
 * caller the same crisp export.
 *
 * The branding logo is a DOM overlay, not part of the SVG, so rasterising the code alone would drop
 * it: `logoUrl` is loaded separately and painted back on at the same proportions. Two things keep
 * that from breaking the download:
 *
 * - `crossOrigin` is set before `src`. The logo comes from Cloudinary, which does send CORS headers,
 *   but without the attribute the browser still marks the canvas tainted and `toBlob` then throws -
 *   silently killing every download, logo or not.
 * - Any failure to load or draw the logo falls through to saving the plain code. A brand mark is a
 *   nicety; the scannable code is the deliverable, and must never be lost to a missing image.
 */
export const downloadSurveyQrCode = (wrapper: HTMLElement | null, fileName: string, logoUrl?: string) => {
    const svg = wrapper?.querySelector('svg')
    if (!svg) return

    const clone = svg.cloneNode(true) as SVGSVGElement
    clone.removeAttribute('style')
    clone.setAttribute('width', String(DOWNLOAD_SIZE))
    clone.setAttribute('height', String(DOWNLOAD_SIZE))

    const svgData = new XMLSerializer().serializeToString(clone)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new Image()

    img.onload = () => {
        canvas.width = DOWNLOAD_SIZE
        canvas.height = DOWNLOAD_SIZE

        if (!ctx) return

        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, DOWNLOAD_SIZE, DOWNLOAD_SIZE)
        ctx.drawImage(img, 0, 0, DOWNLOAD_SIZE, DOWNLOAD_SIZE)

        if (!logoUrl) {
            saveCanvas(canvas, fileName)
            return
        }

        const logo = new Image()
        // Before src, always - see the note above.
        logo.crossOrigin = 'anonymous'
        logo.onload = () => {
            drawCentreLogo(ctx, logo)
            saveCanvas(canvas, fileName)
        }
        logo.onerror = () => saveCanvas(canvas, fileName)
        logo.src = logoUrl
    }

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)))
}
